import { useMemo, useState } from 'react';
import { Link, useParams } from 'react-router';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import {
  Popover,
  PopoverContent,
  PopoverHeader,
  PopoverTitle,
  PopoverTrigger,
} from '@/components/ui/popover';
import { SharedIcon } from 'components/SharedIcon/SharedIcon';
import { useUserNotifications } from 'hooks/useUserNotifications';
import {
  countUnreadNotifications,
  unreadNotificationIds,
} from './notificationReadState';
import NotificationsList from './NotificationsList';
import { useNotificationReadState } from './useNotificationReadState';

export function NotificationsBellMenu() {
  const { incidentId } = useParams<{ incidentId: string }>();
  const { data: userNotifications = [] } = useUserNotifications();
  const { readIds, markAllRead } = useNotificationReadState();
  const [open, setOpen] = useState(false);
  const [flyoutUnreadIds, setFlyoutUnreadIds] = useState<Set<string>>(
    () => new Set()
  );

  const unreadCount = useMemo(
    () => countUnreadNotifications(userNotifications, readIds, incidentId),
    [userNotifications, readIds, incidentId]
  );
  const badgeLabel = unreadCount > 99 ? '99+' : String(unreadCount);

  const handleOpenChange = (nextOpen: boolean) => {
    if (nextOpen) {
      const unread = unreadNotificationIds(
        userNotifications,
        readIds,
        incidentId
      );
      setFlyoutUnreadIds(new Set(unread));
      markAllRead(
        unread,
        userNotifications.map((notification) => notification.notificationId)
      );
    }
    setOpen(nextOpen);
  };

  return (
    <Popover open={open} onOpenChange={handleOpenChange}>
      <PopoverTrigger
        render={
          <Button
            type="button"
            size="icon"
            variant="outline"
            className="tw:relative"
            aria-label={
              unreadCount > 0
                ? `Notifications (${unreadCount})`
                : 'Notifications'
            }
          />
        }
      >
        <SharedIcon iconName="notifications" />
        {unreadCount > 0 ? (
          <Badge
            variant="count-destructive"
            className="tw:pointer-events-none tw:absolute tw:-right-1 tw:-top-1 tw:h-5 tw:min-w-5 tw:px-1"
            aria-label={`${unreadCount} unread notifications`}
          >
            {badgeLabel}
          </Badge>
        ) : null}
      </PopoverTrigger>
      <PopoverContent
        align="end"
        className="dark tw:max-h-[min(36rem,80vh)] tw:w-[26.25rem] tw:overflow-y-auto tw:p-3 tw:text-foreground"
      >
        <PopoverHeader>
          <PopoverTitle>Notifications</PopoverTitle>
        </PopoverHeader>
        <NotificationsList
          filter="active"
          hideFilters
          unreadIds={flyoutUnreadIds}
        />
        <div className="tw:mt-3 tw:border-t tw:border-border tw:pt-2">
          <Link
            to="/hub?openTab=Notifications"
            className="tw:text-xs tw:font-medium tw:text-primary tw:hover:underline"
            onClick={() => setOpen(false)}
          >
            View all notifications
          </Link>
        </div>
      </PopoverContent>
    </Popover>
  );
}
